import './LoiComparisonTab.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStateSelector } from '../../../store/selectors';
import { fetchLoiComparison } from '../../../slices/marketplace/loi-comparison/loi-comparison-api';
import { LoiComparisonFormDto } from '../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import {
    LoiComparisonSubTab,
    LoiComparisonSubTabs,
} from '../../../slices/marketplace/loi-comparison/models/loi-comparison-sub-tab.enum';
import { LoiComparisonSummary } from './LoiComparisonSummary/LoiComparisonSummary';
import { LoiComparisonLegalIssues } from './LoiComparisonLegalIssues/LoiComparisonLegalIssues';
import createNotification from '../../../utils/createNotification';
import Helpers from '../../../utils/helper';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { EngagementProcess } from '../../../slices/marketplace/timeline/models/engagement-process.model';

export interface LoiComparisonTabProps {
    engagementId: number;
    levelOfService: number;
    engagementProcess?: EngagementProcess | null;
    setSearchParams: (params: { [key: string]: string }) => void;
    isSeller: boolean;
    isBroker: boolean;
    isAdmin: boolean;
    loiComparisonEventId: string | null;
    isSelectionFinalized: boolean;
    fetchData: () => void;
}

export const LoiComparisonTab = ({
    engagementId,
    levelOfService,
    engagementProcess = null,
    setSearchParams,
    isSeller,
    isBroker,
    isAdmin,
    loiComparisonEventId,
    isSelectionFinalized,
    fetchData,
}: LoiComparisonTabProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [items, setItems] = useState<LoiComparisonFormDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<number>(
        LoiComparisonSubTab.LoiForm
    );
    const [searchParams] = useSearchParams({});

    const loadItems = useCallback(() => {
        if (!axios) {
            return;
        }
        setIsLoading(true);
        fetchLoiComparison(axios, engagementId)
            .then((data) => {
                setItems(data);
            })
            .catch(() => {
                createNotification(
                    'Could not load LOI comparison. Please try again.',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [axios, engagementId]);

    const handleAfterSelection = useCallback(() => {
        fetchData();
        loadItems();
    }, [fetchData, loadItems]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    useEffect(() => {
        const value = searchParams.get('subtab');
        const numberValue = Number(value);
        if (
            value &&
            numberValue &&
            numberValue >= 1 &&
            numberValue <= LoiComparisonSubTabs.length
        ) {
            setActiveTab(numberValue);
        }
    }, [searchParams]);

    // The server resolves the caller's party per engagement on the LOI comparison
    // endpoint — more reliable than the client-side heuristic props, which can
    // misclassify a seller who also carries a global broker/admin flag.
    const serverParty = items[0]?.currentUserParty;
    const effectiveIsSeller = serverParty != null
        ? serverParty === TimelineParty.Seller || serverParty === TimelineParty.SellerAndBuyer
        : isSeller;
    const effectiveIsBroker = serverParty != null ? serverParty === TimelineParty.Broker : isBroker;
    const effectiveIsAdmin = serverParty != null ? serverParty === TimelineParty.VetValue : isAdmin;

    const renderTabContent = () => {
        if (isLoading) {
            return Helpers.renderTableLoader();
        }
        switch (activeTab) {
            case LoiComparisonSubTab.LoiForm:
                return (
                    <LoiComparisonSummary
                        items={items}
                        levelOfService={levelOfService}
                        engagementProcess={engagementProcess}
                        engagementId={engagementId}
                        isSeller={effectiveIsSeller}
                        isBroker={effectiveIsBroker}
                        isAdmin={effectiveIsAdmin}
                        loiComparisonEventId={loiComparisonEventId}
                        isSelectionFinalized={isSelectionFinalized}
                        fetchData={handleAfterSelection}
                        onSetItems={setItems}
                    />
                );
            case LoiComparisonSubTab.LegalIssues:
                return <LoiComparisonLegalIssues items={items} mode="legalIssues" />;
            case LoiComparisonSubTab.AdditionalNdaCovenants:
                return <LoiComparisonLegalIssues items={items} mode="covenants" />;
            default:
                return <></>;
        }
    };

    return (
        <div className="loi-comparison-tab">
            <div className="body_inner_content">
                <div className="RRT__container">
                    <div className="RRT__tabs body-tabs">
                        {LoiComparisonSubTabs.map((item) => (
                            <div
                                key={item.id}
                                className={
                                    activeTab === item.id
                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                        : 'RRT__tab'
                                }
                                onClick={() => {
                                    setActiveTab(item.id);
                                    setSearchParams({
                                        subtab: item.id.toString(),
                                    });
                                }}>
                                {item.content}
                            </div>
                        ))}
                    </div>
                </div>
                {renderTabContent()}
            </div>
        </div>
    );
};
